/**
 * A check-in's rhythm, as the editor holds it and as the wire takes it (R21).
 *
 * ── Why this is its own module ───────────────────────────────────────────────────────────────
 * The cadence is the one part of the form the backend refuses on its own terms — `ParseCadence` in
 * `Services/SurveyTemplateProcessor.cs` throws for a missing start date, a weekly rhythm with no
 * weekday and a monthly one with no day — and a save that is going to be refused should be refused
 * where the trainer is standing, not after a round trip that loses their draft's place. Keeping the
 * three rules here rather than inside the reducer is what lets the create screen ask the same
 * question before its first POST that the editor asks before every PATCH.
 *
 * ── The frequency changes what the OTHER two fields mean, and neither is cleared ─────────────
 * Switching Co tydzień → Co miesiąc leaves `dayOfWeek` where it was. That is deliberate: a trainer
 * flipping between the two segments to see what each offers would otherwise lose the Monday they
 * had chosen the moment they looked at the monthly option. `toCadenceInput` sends only the field
 * the chosen frequency uses, so the remembered one never reaches the server.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import type { TrainWithMeModulesSurveysDtosSurveyCadenceInput as SurveyCadenceInput } from '~/shared/api/generated/model';

import type { SurveyCadence } from '~/entities/survey-template';

import type { TranslateKey } from './translateKey';

/** The three rhythms a check-in can have — `entities/survey-template`'s, which the list reads too. */
export type { SurveyCadence };

/** What the cadence section holds while it is being edited. */
export interface CadenceDraft {
  readonly frequency: SurveyCadence;
  /** 0 = Sunday … 6 = Saturday, the backend's own numbering. Used only by a weekly rhythm. */
  readonly dayOfWeek: number | null;
  /** 1 … 31. Used only by a monthly rhythm. */
  readonly dayOfMonth: number | null;
  /** `YYYY-MM-DD`, the `<input type="date">` value. Required by every frequency. */
  readonly startDate: string | null;
}

/** What a cadence can be missing. Each one is a refusal the backend would make anyway. */
export type CadenceProblem = 'startDate' | 'dayOfWeek' | 'dayOfMonth';

/**
 * A rhythm with nothing chosen yet.
 *
 * Weekly rather than daily, because it is the one the canon draws and by far the common case; the
 * start date is deliberately absent so the create screen refuses to save until the trainer says
 * when the check-in begins rather than silently starting it today.
 */
export const EMPTY_CADENCE: CadenceDraft = {
  frequency: 'Week',
  dayOfWeek: null,
  dayOfMonth: null,
  startDate: null,
};

/** Every rule `ParseCadence` enforces, asked here first. Empty means the rhythm is complete. */
export function cadenceProblems(cadence: CadenceDraft | null): readonly CadenceProblem[] {
  if (cadence === null) return [];

  const problems: CadenceProblem[] = [];
  // Every frequency needs one, daily included: it is the date occurrences are counted from, not a
  // decoration on the weekly case.
  if (cadence.startDate === null || cadence.startDate === '') problems.push('startDate');
  if (cadence.frequency === 'Week' && cadence.dayOfWeek === null) problems.push('dayOfWeek');
  if (cadence.frequency === 'Month' && cadence.dayOfMonth === null) problems.push('dayOfMonth');
  return problems;
}

/** The sentence under the section when something is missing. */
export function cadenceProblemMessage(problem: CadenceProblem, t: TranslateKey): string {
  return t(`web:reports.editor.cadence.problem.${problem}`);
}

/**
 * The cadence as the wire takes it — only the field this frequency uses.
 *
 * The unused one is dropped rather than sent as null for its own sake: a monthly rhythm carrying a
 * weekday would be a second answer to „which day", and the two would disagree the first time a
 * later read believed the wrong one.
 */
export function toCadenceInput(cadence: CadenceDraft): SurveyCadenceInput {
  return {
    frequency: cadence.frequency,
    dayOfWeek: cadence.frequency === 'Week' ? cadence.dayOfWeek : null,
    dayOfMonth: cadence.frequency === 'Month' ? cadence.dayOfMonth : null,
    startDate: cadence.startDate,
  };
}

/**
 * The seven weekday chips, in the order the canon draws them: Poniedziałek first.
 *
 * The backend numbers weekdays 0 = Sunday … 6 = Saturday and `web:weekdays.short` is keyed 1 = Pn …
 * 7 = N, which is ISO. Both numbers are stated here, once, because two readings of one number is how
 * a survey came to say Monday on one surface and Sunday on another — `surveyList.cadenceLabel`
 * converts in the same direction for the chip it prints.
 */
export const WEEKDAY_CHOICES: readonly { readonly dayOfWeek: number; readonly isoKey: string }[] = [
  { dayOfWeek: 1, isoKey: '1' },
  { dayOfWeek: 2, isoKey: '2' },
  { dayOfWeek: 3, isoKey: '3' },
  { dayOfWeek: 4, isoKey: '4' },
  { dayOfWeek: 5, isoKey: '5' },
  { dayOfWeek: 6, isoKey: '6' },
  { dayOfWeek: 0, isoKey: '7' },
];

/** 1 … 31, the days a monthly rhythm may fall on. */
export const MIN_DAY_OF_MONTH = 1;
export const MAX_DAY_OF_MONTH = 31;
